import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Pagination,
} from '@mui/material';
import { format } from 'date-fns';
import { CheckIn } from '../../types';
import apiService from '../../services/api';

interface CheckInHistoryProps {
  memberId: string;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'success':
      return 'success';
    case 'contract-expired':
    case 'payment-overdue':
      return 'warning';
    case 'suspended':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'success':
      return 'Success';
    case 'contract-expired':
      return 'Contract Expired';
    case 'payment-overdue':
      return 'Payment Overdue';
    case 'suspended':
      return 'Suspended';
    default:
      return 'Unknown';
  }
};

const CheckInHistory: React.FC<CheckInHistoryProps> = ({ memberId }) => {
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchCheckIns = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getMemberCheckIns(memberId, {
        page,
        limit: 10,
      });
      setCheckIns(response.checkIns);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      setError('Failed to load check-in history');
      console.error('Error fetching check-in history:', err);
    } finally {
      setLoading(false);
    }
  }, [memberId, page]);

  useEffect(() => {
    fetchCheckIns();
  }, [fetchCheckIns]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  if (loading && checkIns.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Check-In History
        </Typography>

        {checkIns.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No check-in records found.
          </Typography>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'background.default' }}>
                    <TableCell><strong>Date & Time</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Device</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {checkIns.map((checkIn) => (
                    <TableRow key={checkIn._id}>
                      <TableCell>
                        {format(new Date(checkIn.timestamp), 'PPp')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(checkIn.status)}
                          color={getStatusColor(checkIn.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {checkIn.deviceType === 'tablet' ? 'Gym Entrance Tablet' : 
                         checkIn.deviceType === 'kiosk' ? 'Self-Service Kiosk' : 'Admin System'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CheckInHistory;
